import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Gift, Phone } from "lucide-react";

interface NavItem {
  label: string;
  href: string;
  internal?: boolean;
  number: string;
}

const ModernNav = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const nav: NavItem[] = [
    { number: "01", label: "Warum", href: "#warum" },
    { number: "02", label: "Leistungen", href: "#leistungen" },
    { number: "03", label: "Zeiten", href: "#zeiten" },
    { number: "04", label: "Preise", href: "#preise" },
    { number: "05", label: "FAQ", href: "#faq" },
    { number: "06", label: "Blog", href: "/blog", internal: true },
    { number: "07", label: "Kompakt", href: "/kompakt", internal: true },
    { number: "08", label: "KI-Chatbot", href: "/chatbot-produkt", internal: true },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileMenuOpen(false);
  };
  
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm">
      {/* Top Bar with Contact Info */}
      <div className="bg-slate-900 text-white py-2.5">
        <div className="mx-auto max-w-7xl px-4 md:px-6 flex flex-wrap items-center justify-center md:justify-between gap-2 md:gap-4 text-sm md:text-base">
          <a 
            href="tel:+4915128958006" 
            className="flex items-center gap-2 hover:text-accent transition-colors font-medium tracking-wide"
          >
            <Phone className="h-4 w-4" />
            <span>01512 895 800 6</span>
          </a>
          <a 
            href="https://wa.me/4915128958006?text=Hallo%2C%20ich%20habe%20eine%20Frage%20zum%20IT-Support."
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 px-4 py-1.5 rounded-lg transition-colors font-semibold"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span>WhatsApp</span>
          </a>
        </div>
      </div>
      
      {/* Main Navigation */}
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6 py-4 md:py-5">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_tech-support-mvp/artifacts/88uemucq_LogoKlartech%20%281%29.png" 
            alt="Klartech Müritz Logo" 
            className="h-10 md:h-12 w-auto object-contain"
          />
        </div>
        
        {/* Desktop Navigation - Numbered */}
        <nav className="hidden items-center gap-1 lg:flex">
          {nav.map((n) => (
            n.internal ? (
              <Link
                key={n.href}
                to={n.href}
                className="group relative px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <span className="text-xs text-primary/60 font-mono mr-1.5">{n.number}</span>
                <span className="relative">
                  {n.label}
                  <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            ) : (
              <button
                key={n.href}
                onClick={() => scrollTo(n.href)}
                className="group relative px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                <span className="text-xs text-primary/60 font-mono mr-1.5">{n.number}</span>
                <span className="relative">
                  {n.label}
                  <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                </span>
              </button>
            )
          ))}
        </nav>
        
        <div className="flex items-center gap-3">
          {/* Gift Voucher Button */}
          <Link to="/gutschein">
            <Button
              variant="outline"
              className="hidden md:flex items-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-2.5 text-red-700 font-semibold hover:bg-red-100 hover:border-red-300 transition-all"
            >
              <Gift className="h-4 w-4" />
              Gutschein
            </Button>
          </Link>
          
          {/* Desktop CTA */}
          <Button
            onClick={() => scrollTo("#kontakt")}
            className="hidden md:flex rounded-xl bg-primary hover:bg-primary/90 px-6 py-3 text-primary-foreground shadow-lg transition-all text-sm font-bold tracking-wide"
          >
            Kontakt aufnehmen
          </Button>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Menü öffnen"
          >
            {mobileMenuOpen ? (
              <svg className="h-7 w-7 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-7 w-7 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu - With Numbers */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-background shadow-lg">
          <nav className="flex flex-col px-4 py-4 gap-1">
            {nav.map((n) => (
              n.internal ? (
                <Link
                  key={n.href}
                  to={n.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 text-lg font-medium text-muted-foreground py-3 px-4 rounded-lg hover:bg-muted hover:text-foreground transition-colors"
                >
                  <span className="text-sm text-primary/60 font-mono w-6">{n.number}</span>
                  {n.label}
                </Link>
              ) : (
                <button
                  key={n.href}
                  onClick={() => scrollTo(n.href)}
                  className="flex items-center gap-3 text-left text-lg font-medium text-muted-foreground py-3 px-4 rounded-lg hover:bg-muted hover:text-foreground transition-colors"
                >
                  <span className="text-sm text-primary/60 font-mono w-6">{n.number}</span>
                  {n.label}
                </button>
              )
            ))}
            <Link
              to="/gutschein"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 text-lg font-semibold text-red-700 py-3 px-4 rounded-lg bg-red-50 hover:bg-red-100 transition-colors mt-2"
            >
              <Gift className="h-5 w-5" />
              Gutschein verschenken
            </Link>
            <Button
              onClick={() => scrollTo("#kontakt")}
              className="mt-3 w-full rounded-xl bg-primary hover:bg-primary/90 px-6 py-4 text-primary-foreground shadow-lg transition-all text-lg font-bold"
            >
              Kontakt aufnehmen
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default ModernNav;
